import { z } from 'zod';

import { CUSTOM_BUDGET_TIER_CODE } from '@features/campaigns/shared/types/campaignSubmit.ts';
import { CustomBudgetBounds } from '@features/campaigns/thematicPackage/types/thematicPackage.types.ts';
import { isCustomBudgetAmountWithinBounds } from '@features/campaigns/thematicPackage/utils/resolveCustomBudgetBounds.ts';

export const ThematicPackageUpsellFields = {
  BUDGET_TIER_ID: 'budgetTierId',
  CUSTOM_BUDGET_AMOUNT: 'customBudgetAmount',
  THEMATIC_PACKAGE_ID: 'thematicPackageId',
} as const;

// The fields stay loose and the rules live in the refinement: superRefine only runs once the plain
// shape parses, and each phase submits with the other phase's field still empty.
const thematicPackageUpsellFormSchema = z.object({
  [ThematicPackageUpsellFields.BUDGET_TIER_ID]: z.string(),
  [ThematicPackageUpsellFields.CUSTOM_BUDGET_AMOUNT]: z.number().nullable(),
  [ThematicPackageUpsellFields.THEMATIC_PACKAGE_ID]: z.string(),
});

export type ThematicPackageUpsellFormValues = z.infer<typeof thematicPackageUpsellFormSchema>;

export const thematicPackageUpsellDefaultValues: ThematicPackageUpsellFormValues = {
  [ThematicPackageUpsellFields.BUDGET_TIER_ID]: '',
  [ThematicPackageUpsellFields.CUSTOM_BUDGET_AMOUNT]: null,
  [ThematicPackageUpsellFields.THEMATIC_PACKAGE_ID]: '',
};

export function buildThematicPackageUpsellValidationSchema(customBudgetBounds?: CustomBudgetBounds) {
  return thematicPackageUpsellFormSchema.superRefine(
    ({ budgetTierId, customBudgetAmount, thematicPackageId }, context) => {
      if (!budgetTierId) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'errorMessages.required',
          path: [ThematicPackageUpsellFields.BUDGET_TIER_ID],
        });
      }

      if (
        budgetTierId === CUSTOM_BUDGET_TIER_CODE &&
        !isCustomBudgetAmountWithinBounds(customBudgetAmount, customBudgetBounds)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'errorMessages.numericalValueRequired',
          path: [ThematicPackageUpsellFields.CUSTOM_BUDGET_AMOUNT],
        });
      }

      if (!thematicPackageId) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'errorMessages.required',
          path: [ThematicPackageUpsellFields.THEMATIC_PACKAGE_ID],
        });
      }
    },
  );
}
